import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { getErrorToastMessage } from '@/libs/api'
import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type {
  HookParams,
  UpdateOpeningHoursParams,
  UpdateSpecialOpeningHoursParams,
  WorkingHoursParams,
  WorkingHoursResponse,
} from '@/libs/interfaces'
import { workingHoursApi } from '@/services/api'

export function useStoreOpeningHours({ params, enabled }: HookParams<WorkingHoursParams>) {
  return useQuery<WorkingHoursResponse>({
    queryKey: QUERY_KEYS.workingHours.openingHours(params.storeID),
    queryFn: () => workingHoursApi.getOpeningHours(params),
    enabled: (enabled ?? true) && !!params.storeID,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useStoreOpeningHoursUpdate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (params: UpdateOpeningHoursParams) => workingHoursApi.updateOpeningHours(params),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.workingHours.all,
      })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useStoreSpecialOpeningHoursUpdate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (params: UpdateSpecialOpeningHoursParams) =>
      workingHoursApi.updateSpecialOpeningHours(params),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.workingHours.all,
      })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}
